type MarloLogoState = 'idle' | 'thinking' | 'success' | 'idea'

type MarloLogoProps = {
  state?: MarloLogoState
  size?: number
  showWordmark?: boolean
  className?: string
}

type MarloSuggestionLogoStateParams = {
  isLoading: boolean
  suggestionCount: number
  selectedSuggestion: number | 'custom' | null
}

export function getMarloSuggestionLogoState({
  isLoading,
  suggestionCount,
  selectedSuggestion,
}: MarloSuggestionLogoStateParams): MarloLogoState {
  if (isLoading) {
    return 'thinking'
  }

  if (selectedSuggestion !== null) {
    return 'success'
  }

  if (suggestionCount > 0) {
    return 'idea'
  }

  return 'idle'
}

export function MarloLogo({ state = 'idle', size = 36, showWordmark = true, className }: MarloLogoProps) {
  const mouth =
    state === 'success'
      ? 'M26 53Q40 64 54 53'
      : state === 'idea'
        ? 'M34 58Q40 60 46 58'
        : 'M28 56Q40 62 53 54'

  const iconClassName = `marlo-logo__icon marlo-logo__icon--${state}`
  const rootClassName = ['logo', 'marlo-logo', className].filter(Boolean).join(' ')

  return (
    <div className={rootClassName} aria-label="marlo">
      <svg
        aria-hidden="true"
        className={iconClassName}
        style={{ width: size, height: size }}
        viewBox="0 0 80 80"
        xmlns="http://www.w3.org/2000/svg"
      >
        {state === 'thinking' ? (
          <>
            <circle className="marlo-logo__thought marlo-logo__thought--1" cx="20" cy="10" r="2.5" />
            <circle className="marlo-logo__thought marlo-logo__thought--2" cx="30" cy="6" r="3" />
            <circle className="marlo-logo__thought marlo-logo__thought--3" cx="42" cy="10" r="2.5" />
          </>
        ) : null}
        {state === 'idea' ? <ellipse className="marlo-logo__idea" cx="40" cy="58" rx="4" ry="5" /> : null}

        <rect className="marlo-logo__face" width="80" height="80" rx="24" />

        {state === 'thinking' ? (
          <>
            <path className="marlo-logo__line" d="M19 33Q25 28 31 33" />
            <path className="marlo-logo__line" d="M49 33Q55 28 61 33" />
          </>
        ) : (
          <>
            <circle className="marlo-logo__eye-white" cx="25" cy="33" r={state === 'idea' ? 6.5 : 5.5} />
            <circle className="marlo-logo__eye-white" cx="55" cy="33" r={state === 'idea' ? 6.5 : 5.5} />
            <circle className="marlo-logo__pupil" cx="26" cy="31.5" r={state === 'idea' ? 2 : 1.8} />
            {state !== 'success' ? <circle className="marlo-logo__pupil" cx="56" cy="31.5" r={state === 'idea' ? 2 : 1.8} /> : null}
          </>
        )}

        {state === 'success' ? <path className="marlo-logo__line" d="M49 33Q55 30 61 33" /> : null}

        <path className="marlo-logo__brow" d={state === 'idea' ? 'M18 18Q26 14 33 19' : 'M18 21L33 21'} />
        <path
          className="marlo-logo__brow"
          d={state === 'idea' ? 'M47 19Q54 14 62 18' : state === 'success' ? 'M47 21L62 21' : 'M47 19L62 21'}
        />
        <path className="marlo-logo__mouth" d={mouth} />
      </svg>

      {showWordmark ? <div className="logo-text">marlo</div> : null}
    </div>
  )
}

export type { MarloLogoProps, MarloLogoState }